import React from "react";
import { User, MessageSquare, Gem, Settings, Target, ChevronLeft } from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface ProcessStep {
  number: number;
  title: string;
  description: string;
  icon: LucideIcon;
}

const processSteps: ProcessStep[] = [
  {
    number: 1,
    title: "אבחון עם מאמן/ת",
    description: "שיחה אישית להבנת הצרכים והדפוסים שחשובים לך באמת כדי להרגיש בביטחון.",
    icon: User,
  },
  {
    number: 2,
    title: "הגדרת שפת תקשורת",
    description: "תיאום ציפיות וסנכרון סגנון התקשורת שלך, כדי להבטיח התאמה לפרטנר/ית משלימ/ה.",
    icon: MessageSquare,
  },
  {
    number: 3,
    title: 'בניית "מצפן" אישי',
    description: "ריכוז ערכי הליבה, אורח החיים והקווים האדומים שלך לכדי פרופיל מאומת ומדויק.",
    icon: Gem,
  },
  {
    number: 4,
    title: "סינון חכם ואנושי",
    description: "שילוב בין אלגוריתם מתקדם לבדיקה ידנית של הצוות, להבטחת התאמה רגשית עמוקה.",
    icon: Settings,
  },
  {
    number: 5,
    title: "הצעות וליווי אישי",
    description: "קבלת הצעות ממוקדות וליווי הכולל משוב אחרי דייטים, עד למציאת הקשר היציב.",
    icon: Target,
  },
];

const ProcessStepMobile = ({ step, index }: { step: ProcessStep; index: number }) => {
  const Icon = step.icon;
  
  return (
    <div 
      className="flex gap-4 items-start p-4 rounded-xl border border-gold/20 bg-navy/30 backdrop-blur-sm animate-fade-up opacity-0"
      style={{ animationDelay: `${index * 100}ms`, animationFillMode: "forwards" }}
    >
      <div className="flex-shrink-0 w-14 h-14 rounded-full bg-gradient-gold flex items-center justify-center shadow-gold">
        <Icon className="w-6 h-6 text-navy" />
      </div>
      <div className="flex-1 pt-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-gold font-bold text-sm">{step.number}</span>
          <h3 className="font-serif text-lg font-bold text-cream">{step.title}</h3>
        </div>
        <p className="text-cream/80 text-sm leading-relaxed">{step.description}</p>
      </div>
    </div>
  );
};

const ProcessStepDesktop = ({ step, index }: { step: ProcessStep; index: number }) => {
  const Icon = step.icon;
  
  return (
    <div 
      className="flex flex-col items-center text-center max-w-[180px] animate-fade-up opacity-0"
      style={{ animationDelay: `${index * 100}ms`, animationFillMode: "forwards" }}
    >
      <div className="relative mb-3">
        <div className="w-16 h-16 rounded-full bg-gradient-gold flex items-center justify-center shadow-gold transition-transform duration-300 hover:scale-110">
          <Icon className="w-7 h-7 text-navy" />
        </div>
        <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-navy border-2 border-gold text-gold text-xs font-bold flex items-center justify-center">
          {step.number}
        </span>
      </div>
      <h3 className="font-serif text-base font-bold text-cream mb-2">{step.title}</h3>
      <p className="text-cream/70 text-xs leading-relaxed">{step.description}</p>
    </div>
  );
};

const HomeProcessSection = () => {
  return (
    <section className="mt-12 mb-10" aria-labelledby="process-heading">
      <h2 
        id="process-heading"
        className="text-2xl md:text-3xl font-serif font-bold text-cream mb-8 text-center animate-fade-up opacity-0"
        style={{ animationFillMode: "forwards" }}
      >
        איך זה עובד? המסלול שלך לזוגיות
      </h2>
      
      {/* מובייל - סידור אנכי */}
      <div className="lg:hidden space-y-4 px-2">
        {processSteps.map((step, index) => (
          <ProcessStepMobile key={step.number} step={step} index={index} />
        ))}
      </div>
      
      {/* דסקטופ - סידור אופקי עם חצים */}
      <div className="hidden lg:flex items-start justify-center gap-6">
        {processSteps.map((step, index) => (
          <React.Fragment key={step.number}>
            <ProcessStepDesktop step={step} index={index} />
            {index < processSteps.length - 1 && (
              <ChevronLeft 
                className="text-gold/50 mt-6 flex-shrink-0 w-5 h-5 animate-fade-up opacity-0" 
                style={{ animationDelay: `${index * 100 + 50}ms`, animationFillMode: "forwards" }}
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </section>
  );
};

export default HomeProcessSection;
